import { useMemo } from 'react';
import { uniqBy } from 'lodash';
import { Feature, FeatureCollection } from 'geojson';
import { formatSoilFeatureName } from '../../utils/soils';
import DiscreteKey from '../right-menu/DiscreteKey';
import {
  ColorKey,
  LegendItem,
  SoilAttribute,
  SoilAttributeData,
} from '../../types/soilAttributes';

interface SoilDiscreteKeyProps {
  activeAttribute: SoilAttribute;
  activeData: Nullable<SoilAttributeData>;
  activeKey?: Nullable<string>;
  colorKey: Nullable<ColorKey>;
  data: FeatureCollection;
  legend?: Nullable<LegendItem[]>;
  onClick?: (e: MouseEvent, hexCode: string, label: string) => void;
}

const SoilDiscreteKey = ({
  activeAttribute,
  activeData,
  activeKey,
  colorKey,
  data,
  legend,
  onClick,
}: SoilDiscreteKeyProps) => {
  const keys = useMemo(
    () =>
      legend ||
      uniqBy(
        data.features
          .filter((f: Feature) => f.properties != null)
          .map((f: Feature) => ({
            hexCode: f.properties?.rgbString,
            label: formatSoilFeatureName(f),
          })),
        'hexCode',
      ),
    [legend, data.features],
  );

  const activeFeature = useMemo(
    () => data.features.find((f: Feature) => f.id === activeKey),
    [activeKey, data.features],
  );

  const colorToHighlight = useMemo(() => {
    if (!activeFeature?.properties) return null;
    return colorKey
      ? colorKey[activeFeature.properties.mapUnitKey]
      : activeFeature.properties.rgbString;
  }, [activeFeature, colorKey]);

  const colorsLoading =
    activeData?.loading &&
    (activeAttribute.interpRatingShortFormData ||
      activeAttribute.reportShortFormData);

  return colorsLoading ? (
    'Loading...'
  ) : (
    <>
      {activeAttribute.units && `(${activeAttribute.units})`}
      <DiscreteKey
        colorToHighlight={colorToHighlight}
        keys={keys}
        maxHeight="476px"
        onClick={onClick}
      />
    </>
  );
};

export default SoilDiscreteKey;
